import { useCallback, useMemo, useState, useEffect } from 'react';
import hubHttpClient from 'hub-http/clients/apiClient';
import { useReactNodeView } from '../utils/ReactNodeView';
import { Popover } from 'foundations-components/transitional/components/Popover';
import MeetingsPopover from '../commands/meetings/MeetingsPopover';
import { MEETINGS } from 'hubspot-prosemirror/prosemirror/editorCapabilities';
import { fetchMeetings } from '../commands/meetings/fetchMeetings';
import Raven from 'raven-js';
import MeetingLink from '../commands/meetings/MeetingLink';
import { SENDER_MEETINGS_LINK_TOKEN, MEETINGS_LINK_TYPES as LINK_TYPES } from 'hubspot-prosemirror/draft-transplant/utils/constants';
import { FocusPlugin } from '../plugins/FocusPlugin';
import { jsx as _jsx } from "react/jsx-runtime";
export default function MeetingsView() {
  var _editorConfig$MEETING, _node$attrs;
  const {
    node,
    view,
    getPos,
    editorConfig
  } = useReactNodeView();
  const [open, setOpen] = useState(false);
  const [meetingLinks, setMeetingLinks] = useState([]);
  const [hasFetchMeetingDone, setHasFetchMeetingDone] = useState(false);
  const allowSenderMeetingLink = editorConfig === null || editorConfig === void 0 || (_editorConfig$MEETING = editorConfig[MEETINGS]) === null || _editorConfig$MEETING === void 0 ? void 0 : _editorConfig$MEETING.allowSenderMeetingLink;
  const {
    meetingURL,
    meetingName
  } = (_node$attrs = node === null || node === void 0 ? void 0 : node.attrs) !== null && _node$attrs !== void 0 ? _node$attrs : {};
  useEffect(() => {
    if (!hasFetchMeetingDone) {
      fetchMeetings({
        httpClient: hubHttpClient
      }).then(meetings => {
        setMeetingLinks(meetings);
      }).catch(error => {
        Raven.captureException(error);
      }).finally(() => {
        setHasFetchMeetingDone(true);
      });
    }
  }, [hasFetchMeetingDone]);
  const onOpenChange = useCallback(e => {
    if (e.target) {
      setOpen(e.target.value);
    }
  }, [setOpen]);
  const handleClick = useCallback(() => {
    if (!(view !== null && view !== void 0 && view.editable)) return;
    setOpen(prevState => !prevState);
  }, [setOpen, view]);
  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);
  const updateMeeting = useCallback(({
    meetingURL: updatedMeetingURL,
    meetingName: updatedMeetingName
  }) => {
    if (view) {
      // adding a delay to ensure the popover closes before the transaction is dispatched
      setTimeout(() => {
        // update this node in place; the selection is not guaranteed to be on it
        const pos = getPos === null || getPos === void 0 ? void 0 : getPos();
        if (typeof pos !== 'number') return;
        const meetingNode = view.state.doc.nodeAt(pos);
        if (!meetingNode) return;
        view.dispatch(view.state.tr.setNodeMarkup(pos, undefined, {
          meetingURL: updatedMeetingURL,
          meetingName: updatedMeetingName
        }, meetingNode.marks).setMeta(FocusPlugin, {
          focusView: true,
          focusViewPos: pos + 1 // after the meeting link
        }));
      }, 50);
    }
  }, [view, getPos]);
  const meetingPopover = useMemo(() => {
    const initialMeetingLinkType = meetingURL === SENDER_MEETINGS_LINK_TOKEN ? LINK_TYPES.SENDER : LINK_TYPES.CUSTOM;
    const initialMeetingName = meetingName === SENDER_MEETINGS_LINK_TOKEN ? '' : meetingName;
    return () => /*#__PURE__*/_jsx(MeetingsPopover, {
      initialMeetingName: initialMeetingName,
      initialMeetingLinkType: initialMeetingLinkType,
      initialMeetingURL: meetingURL,
      onChange: updateMeeting,
      onClose: handleClose,
      allowSenderMeetingLink: allowSenderMeetingLink,
      isNew: false
    });
  }, [allowSenderMeetingLink, handleClose, meetingName, meetingURL, updateMeeting]);
  return /*#__PURE__*/_jsx(Popover, {
    open: open,
    onOpenChange: onOpenChange,
    Content: meetingPopover,
    children: /*#__PURE__*/_jsx("span", {
      "data-test-id": "ProseMirror__meetings",
      children: /*#__PURE__*/_jsx(MeetingLink, {
        meetingName: meetingName,
        meetingURL: meetingURL,
        meetingLinks: meetingLinks,
        hasFetchMeetingDone: hasFetchMeetingDone,
        onClick: handleClick
      })
    })
  });
}
export const meetingsViewUpdateFn = ({
  previousNode,
  node
}) => {
  if (previousNode.attrs.meetingURL !== node.attrs.meetingURL || previousNode.attrs.meetingName !== node.attrs.meetingName) {
    return false;
  }
  return true;
};